import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Building2, Landmark, Tag, Users, UsersRound } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/layout/AppLayout";
import { SearchInput } from "@/components/common/Layout";
import { GroupIcon } from "@/components/common/Visuals";
import { ErrorState } from "@/components/common/States";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchResults } from "@/components/home/SearchResults";
import { KINDS } from "@/lib/kinds";
import { formatNumber } from "@/lib/format";

const STATS = [
  { key: "persons", label: "Personnes", icon: Users, to: "/personnes", color: "#2563EB" },
  { key: "organizations", label: "Organisations", icon: Building2, to: "/entreprises", color: "#16A34A" },
  { key: "clubs", label: "Clubs", icon: UsersRound, to: "/clubs", color: "#A4247F" },
  { key: "commissions", label: "Commissions", icon: Landmark, to: "/commissions", color: "#EC7F13" },
  { key: "expertises", label: "Expertises", icon: Tag, to: "/expertises", color: "#0D9488" },
];

function Stats() {
  const { data } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/stats")).data });
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="home-stats">
      {STATS.map(({ key, label, icon: Icon, to, color }, i) => (
        <Link key={key} to={to} data-testid={`stat-${key}`} style={{ animationDelay: `${i * 60}ms` }}
          className="lm-card lm-card-hover lm-fade-up group flex items-center gap-3 p-4">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ backgroundColor: `${color}14`, color }}>
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <div className="font-heading text-2xl font-extrabold leading-none" data-testid={`stat-${key}-value`}>
              {data ? formatNumber(data[key]) : <Skeleton className="h-6 w-10" />}
            </div>
            <div className="mt-1 text-xs font-semibold text-stone-500">{label}</div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function QuickAccess() {
  const { data, isLoading } = useQuery({ queryKey: ["featured"], queryFn: async () => (await api.get("/featured")).data });
  if (isLoading) return <Skeleton className="h-28 w-full rounded-2xl" />;
  const kinds = Object.values(KINDS).filter((k) => data?.[k.key]?.length);
  if (!kinds.length) return null;
  return (
    <section data-testid="quick-access">
      <h2 className="text-xl font-extrabold">Accès rapides</h2>
      <p className="mt-1 text-sm text-muted-foreground">Un clic pour ouvrir le répertoire déjà filtré.</p>
      <div className="mt-5 space-y-5">
        {kinds.map((k) => (
          <div key={k.key} className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <span className="w-32 shrink-0 text-xs font-bold uppercase tracking-[0.16em] text-stone-500">{k.label}</span>
            <div className="flex flex-wrap gap-2.5">
              {data[k.key].map((g) => (
                <Link key={g.id} to={`/personnes?${k.filter}=${g.id}`} data-testid={`quick-access-${g.id}`}
                  className="group inline-flex items-center gap-2.5 rounded-2xl border bg-white py-2 pl-2 pr-4 font-semibold transition-[transform,box-shadow,border-color] hover:-translate-y-0.5 hover:shadow-md"
                  style={{ borderColor: `${g.color || "#A4247F"}40` }}>
                  <span className="h-8 w-8 rounded-xl"><GroupIcon group={g} box /></span>
                  <span style={{ color: g.color || undefined }}>{g.name}</span>
                  <ArrowUpRight className="h-3.5 w-3.5 text-stone-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function HomePage() {
  const [sp, setSp] = useSearchParams();
  const query = sp.get("q") || "";
  const search = useQuery({
    queryKey: ["search", query],
    queryFn: async () => (await api.get(`/search?q=${encodeURIComponent(query)}&limit=6`)).data,
    enabled: query.trim().length > 0,
    placeholderData: (prev) => prev,
  });

  return (
    <div>
      <section className="lm-hero pb-24 pt-14 sm:pt-20">
        <div className="lm-container">
          <div className="mx-auto max-w-3xl text-center lm-fade-up">
            <Logo className="mx-auto h-20 sm:h-24" />
            <h1 className="mt-6 text-4xl font-extrabold sm:text-5xl lg:text-6xl" data-testid="home-title">
              Annuaire <span className="lm-gradient-text">La Mêlée</span>
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-base text-stone-600 sm:text-lg">
              Les personnes, organisations et communautés de l'écosystème numérique d'Occitanie, en quelques secondes.
            </p>
            <SearchInput
              large
              autoFocus
              className="mt-9"
              value={query}
              onChange={(v) => setSp(v ? { q: v } : {}, { replace: true })}
              placeholder="Rechercher une personne, une entreprise, une fonction..."
              testId="global-search-input"
            />
          </div>
        </div>
      </section>
      <div className="lm-container -mt-8 space-y-12 pb-20">
        {query.trim() ? (
          search.isError ? <ErrorState onRetry={search.refetch} /> :
          search.data ? <SearchResults data={search.data} query={query} /> :
          <div className="grid gap-5 lg:grid-cols-2"><Skeleton className="h-64 rounded-2xl" /><Skeleton className="h-64 rounded-2xl" /></div>
        ) : (
          <>
            <Stats />
            <QuickAccess />
          </>
        )}
      </div>
    </div>
  );
}
